/** Mirrors the server's rules for quiz answers (POST/PUT onboarding-test) so
 * the dialog can show an inline message before a doomed request. The server
 * still enforces them; this is only the UX. */
export const MAX_ANSWER_KEYS = 64;
export const MAX_ANSWER_KEY_LENGTH = 64;
export const MAX_ANSWER_STRING_LENGTH = 500;

export type QuizAnswers = Record<string, string | string[]>;

/** `null` when valid, else a one-line message naming the first violation. */
export function validateAnswers(value: unknown): string | null {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return 'Answers must be a JSON object.';
  }
  const entries = Object.entries(value as Record<string, unknown>);
  if (entries.length > MAX_ANSWER_KEYS) {
    return `At most ${MAX_ANSWER_KEYS} answers are allowed (got ${entries.length}).`;
  }
  for (const [key, val] of entries) {
    if (key.length === 0) return 'Answer keys must not be empty.';
    if (key.length > MAX_ANSWER_KEY_LENGTH) {
      return `Key "${key.slice(0, 20)}…" is longer than ${MAX_ANSWER_KEY_LENGTH} characters.`;
    }
    const items = Array.isArray(val) ? val : [val];
    if (!items.every((item) => typeof item === 'string')) {
      return `"${key}" must be a string or an array of strings.`;
    }
    if (items.some((item) => (item as string).length > MAX_ANSWER_STRING_LENGTH)) {
      return `"${key}" has a string longer than ${MAX_ANSWER_STRING_LENGTH} characters.`;
    }
  }
  return null;
}

export type ParsedAnswers = { answers: QuizAnswers | null; error: string | null };

/** An empty editor means "no quiz document" (`answers: null`). */
export function parseAnswersText(text: string): ParsedAnswers {
  if (text.trim() === '') return { answers: null, error: null };
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { answers: null, error: 'Invalid JSON.' };
  }
  const error = validateAnswers(parsed);
  return error ? { answers: null, error } : { answers: parsed as QuizAnswers, error: null };
}
